import {
  Calendar,
  CheckCircle2,
  ExternalLink,
  FolderOpen,
  LayoutDashboard,
  Lightbulb,
  MinusCircle,
  Sparkles,
  Table2,
  Tag,
  TrendingUp,
} from 'lucide-react';
import type { GeneratedApp } from '@/types';
import {
  asMatrix,
  asRecord,
  asStringArray,
  type AppDocument,
  type AppDocumentComponent,
} from '@/components/apps/appDocument';
import { sanitizeAppText } from '@/utils';

function DocHeader({
  title,
  subtitle,
  compact,
}: {
  title: string;
  subtitle?: string;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="rounded-lg border border-primary-500/20 bg-gradient-to-br from-primary-500/15 to-transparent p-3">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-primary-300 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-khiimori-text-primary line-clamp-2">{title}</h2>
            {subtitle && (
              <p className="mt-1 text-xs text-khiimori-text-secondary line-clamp-2 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-primary-500/20 bg-gradient-to-br from-primary-500/15 via-primary-500/5 to-transparent p-5 md:p-6">
      <div className="flex items-start gap-3">
        <Sparkles className="w-6 h-6 text-primary-300 shrink-0 mt-0.5" />
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-khiimori-text-primary">{title}</h2>
          {subtitle && (
            <p className="mt-2 text-sm md:text-base text-khiimori-text-secondary leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ComparisonTableBlock({
  comp,
  compact,
}: {
  comp: AppDocumentComponent;
  compact?: boolean;
}) {
  const columns = asStringArray(comp.columns);
  const rawRows = Array.isArray(comp.rows) ? comp.rows : [];

  let matrixRows: string[][] = [];
  if (rawRows.length > 0 && asRecord(rawRows[0])?.feature != null) {
    for (const row of rawRows) {
      const r = asRecord(row);
      if (!r) continue;
      const feature = typeof r.feature === 'string' ? r.feature : 'Feature';
      const values = Array.isArray(r.values) ? r.values.map(String) : [];
      matrixRows.push([feature, ...values]);
    }
  } else {
    matrixRows = asMatrix(comp.rows);
  }

  const header = columns.length > 0 ? columns : matrixRows[0] ?? [];
  const bodyAll = columns.length > 0 ? matrixRows : matrixRows.slice(1);
  const body = compact ? bodyAll.slice(0, 3) : bodyAll;

  if (header.length === 0) return null;

  return (
    <section className={compact ? 'space-y-1.5' : 'space-y-3'}>
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <Table2 className="w-3.5 h-3.5" />
        <span className="text-[10px] font-semibold uppercase tracking-wide">
          {typeof comp.title === 'string' ? comp.title : 'Comparison table'}
        </span>
      </div>
      <div className={`overflow-x-auto rounded-lg border border-khiimori-border/20 ${compact ? '' : 'shadow-sm'}`}>
        <table className="min-w-full text-sm">
          <thead className="bg-khiimori-bg-elevated/50">
            <tr>
              {header.map((cell, ci) => (
                <th
                  key={ci}
                  className={`text-left font-semibold text-khiimori-text-primary border-b border-khiimori-border/20 ${
                    ci === 0 ? 'sticky left-0 bg-khiimori-bg-elevated/95 z-10' : ''
                  } ${compact ? 'px-2 py-1.5 text-[10px]' : 'px-4 py-3 text-xs'}`}
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr
                key={ri}
                className={`border-b border-khiimori-border/10 last:border-0 ${
                  ri % 2 === 1 ? 'bg-khiimori-bg-elevated/10' : ''
                }`}
              >
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`align-top ${
                      ci === 0
                        ? 'font-medium text-khiimori-text-primary sticky left-0 bg-inherit z-[1]'
                        : 'text-khiimori-text-secondary'
                    } ${compact ? 'px-2 py-1.5 text-[10px] line-clamp-2' : 'px-4 py-3 text-xs'}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FeatureHighlightBlock({ comp }: { comp: AppDocumentComponent }) {
  const items = Array.isArray(comp.items) ? comp.items : [];
  const title = typeof comp.title === 'string' ? comp.title : 'Key differentiators';

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-khiimori-text-primary">{title}</h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, i) => {
          const it = asRecord(item);
          const label =
            typeof it?.laptop === 'string'
              ? it.laptop
              : typeof it?.name === 'string'
                ? it.name
                : `Option ${i + 1}`;
          const highlight =
            typeof it?.highlight === 'string'
              ? it.highlight
              : typeof it?.description === 'string'
                ? it.description
                : '';
          return (
            <div
              key={i}
              className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-300 mb-1">
                {label}
              </p>
              <p className="text-sm text-khiimori-text-secondary">{highlight}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ProsConsBlock({ comp }: { comp: AppDocumentComponent }) {
  const items = Array.isArray(comp.items) ? comp.items : [];

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-khiimori-text-primary">Pros & cons</h3>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((item, i) => {
          const it = asRecord(item);
          const label = typeof it?.laptop === 'string' ? it.laptop : `Option ${i + 1}`;
          const pros = asStringArray(it?.pros);
          const cons = asStringArray(it?.cons);
          return (
            <div
              key={i}
              className="rounded-lg border border-khiimori-border/15 bg-khiimori-bg-elevated/10 p-4"
            >
              <p className="font-medium text-khiimori-text-primary mb-3">{label}</p>
              <div className="space-y-3">
                {pros.length > 0 && (
                  <ul className="space-y-1.5">
                    {pros.map((p, pi) => (
                      <li key={pi} className="flex gap-2 text-sm text-emerald-300/90">
                        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {cons.length > 0 && (
                  <ul className="space-y-1.5">
                    {cons.map((c, ci) => (
                      <li key={ci} className="flex gap-2 text-sm text-rose-300/90">
                        <MinusCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TimelineBlock({ comp }: { comp: AppDocumentComponent }) {
  const events = Array.isArray(comp.events) ? comp.events : [];
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <Calendar className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Timeline</span>
      </div>
      <div className="relative pl-6 border-l-2 border-primary-500/30 space-y-5">
        {events.map((ev, i) => {
          const e = asRecord(ev);
          if (!e) return null;
          const date = typeof e.date === 'string' ? e.date : '';
          const title = sanitizeAppText(typeof e.title === 'string' ? e.title : 'Event', 200) || 'Event';
          const description = sanitizeAppText(typeof e.description === 'string' ? e.description : '', 400);
          const url = typeof e.source_url === 'string' ? e.source_url : '';
          return (
            <div key={i} className="relative">
              <span className="absolute -left-[1.6rem] top-1 w-3 h-3 rounded-full bg-primary-500 ring-4 ring-khiimori-bg-primary" />
              {date && <time className="text-xs font-medium text-primary-300 block mb-1">{date}</time>}
              <h4 className="font-semibold text-khiimori-text-primary">{title}</h4>
              {description && (
                <p className="text-sm text-khiimori-text-secondary mt-1 break-words">{description}</p>
              )}
              {url && (
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-400 hover:text-primary-300 mt-1 inline-flex items-center gap-1">
                  Source <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ResearchSectionsBlock({ comp }: { comp: AppDocumentComponent }) {
  const sections = Array.isArray(comp.sections) ? comp.sections : [];
  return (
    <div className="space-y-3">
      {sections.map((sec, i) => {
        const s = asRecord(sec);
        if (!s) return null;
        const title = typeof s.title === 'string' ? s.title : `Section ${i + 1}`;
        const bullets = asStringArray(s.bullets);
        const body =
          (typeof s.content === 'string' && s.content.trim()) ||
          (typeof s.text === 'string' && s.text.trim()) ||
          (typeof s.body === 'string' && s.body.trim()) ||
          '';
        return (
          <div key={i} className="rounded-lg border border-khiimori-border/15 bg-khiimori-bg-elevated/10 p-4">
            <h4 className="text-sm font-semibold text-khiimori-text-primary mb-2">{title}</h4>
            {body ? (
              <p className="text-sm text-khiimori-text-secondary leading-relaxed whitespace-pre-wrap">{body}</p>
            ) : null}
            {bullets.length > 0 && (
              <ul className={`space-y-1.5 ${body ? 'mt-3' : ''}`}>
                {bullets.map((b, bi) => (
                  <li key={bi} className="text-sm text-khiimori-text-secondary flex gap-2">
                    <span className="text-primary-400">•</span>
                    {b}
                  </li>
                ))}
              </ul>
            )}
            {!body && bullets.length === 0 && (
              <p className="text-xs text-khiimori-text-muted">No content in this section.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SourcesBlock({ comp }: { comp: AppDocumentComponent }) {
  const items = Array.isArray(comp.items) ? comp.items : [];
  if (items.length === 0) return null;
  return (
    <section>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-khiimori-text-muted mb-2">Sources</h4>
      <div className="flex flex-wrap gap-2">
        {items.map((src, i) => {
          const s = asRecord(src);
          const title = typeof s?.title === 'string' ? s.title : 'Source';
          const url = typeof s?.url === 'string' ? s.url : '';
          return url ? (
            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 rounded-full border border-khiimori-border/20 text-primary-300 hover:bg-primary-500/10 inline-flex items-center gap-1">
              {title} <ExternalLink className="w-3 h-3" />
            </a>
          ) : null;
        })}
      </div>
    </section>
  );
}

function CollectionsBlock({ comp }: { comp: AppDocumentComponent }) {
  const groups = Array.isArray(comp.groups) ? comp.groups : [];
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <FolderOpen className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Collections</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {groups.map((col, i) => {
          const c = asRecord(col);
          if (!c) return null;
          const name = typeof c.name === 'string' ? c.name : `Collection ${i + 1}`;
          const description = typeof c.description === 'string' ? c.description : '';
          const items = Array.isArray(c.items) ? c.items : [];
          return (
            <div key={i} className="rounded-lg border border-khiimori-border/15 bg-khiimori-bg-elevated/10 p-4">
              <h4 className="font-semibold text-khiimori-text-primary">{name}</h4>
              {description && <p className="text-xs text-khiimori-text-muted mt-1">{description}</p>}
              <ul className="mt-3 space-y-2">
                {items.map((item, ii) => {
                  const it = asRecord(item);
                  const title = typeof it?.title === 'string' ? it.title : 'Item';
                  const url = typeof it?.url === 'string' ? it.url : '';
                  return (
                    <li key={ii} className="text-sm">
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-300 hover:text-primary-200 inline-flex items-center gap-1">
                          <ExternalLink className="w-3.5 h-3.5" /> {title}
                        </a>
                      ) : (
                        <span className="text-khiimori-text-secondary">{title}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function EntitiesBlock({ comp }: { comp: AppDocumentComponent }) {
  const items = Array.isArray(comp.items) ? comp.items : [];
  const styles: Record<string, string> = {
    product: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
    location: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    person: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  };
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <Tag className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Entities</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((ent, i) => {
          const e = asRecord(ent);
          if (!e) return null;
          const type = typeof e.type === 'string' ? e.type : 'entity';
          const value = typeof e.value === 'string' ? e.value : '';
          const style = styles[type] ?? 'bg-khiimori-bg-elevated/30 text-khiimori-text-secondary border-khiimori-border/20';
          return (
            <span key={i} className={`inline-flex flex-col gap-0.5 px-3 py-2 rounded-lg border text-sm ${style}`}>
              <span className="text-[10px] uppercase opacity-70">{type}</span>
              <span className="font-medium">{value}</span>
            </span>
          );
        })}
      </div>
    </section>
  );
}

function DashboardMetricsBlock({
  comp,
  compact,
}: {
  comp: AppDocumentComponent;
  compact?: boolean;
}) {
  const widgets = Array.isArray(comp.widgets) ? comp.widgets : [];
  const visible = compact ? widgets.slice(0, 3) : widgets;

  return (
    <section className={compact ? 'space-y-2' : 'space-y-3'}>
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <LayoutDashboard className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Key metrics</span>
      </div>
      <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
        {visible.map((w, i) => {
          const widget = asRecord(w);
          if (!widget) return null;
          const title = typeof widget.title === 'string' ? widget.title : `Metric ${i + 1}`;
          const subtitle = typeof widget.subtitle === 'string' ? widget.subtitle : '';
          const value = typeof widget.value === 'string' ? widget.value : '—';
          const change = typeof widget.change === 'string' ? widget.change : '';
          const url = typeof widget.source_url === 'string' ? widget.source_url : '';
          const inner = (
            <div
              className={`rounded-xl border border-khiimori-border/20 bg-khiimori-bg-elevated/10 p-4 h-full transition-colors ${
                url ? 'hover:border-primary-500/30 hover:bg-primary-500/5' : ''
              }`}
            >
              <p className="text-xs text-khiimori-text-muted truncate">{subtitle}</p>
              <p className={`font-semibold text-khiimori-text-primary mt-1 ${compact ? 'text-sm line-clamp-2' : 'text-base'}`}>
                {title}
              </p>
              <p className={`font-bold text-primary-300 mt-2 ${compact ? 'text-lg' : 'text-2xl'}`}>{value}</p>
              {change && (
                <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {change}
                </p>
              )}
            </div>
          );
          return url ? (
            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
              {inner}
            </a>
          ) : (
            <div key={i}>{inner}</div>
          );
        })}
      </div>
    </section>
  );
}

function DashboardSectionsBlock({ comp }: { comp: AppDocumentComponent }) {
  return <ResearchSectionsBlock comp={comp} />;
}

function SuggestionsBlock({ suggestions }: { suggestions: string[] }) {
  if (suggestions.length === 0) return null;
  return (
    <section className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="w-4 h-4 text-amber-300" />
        <h3 className="text-sm font-semibold text-amber-200">Suggested improvements</h3>
      </div>
      <ul className="space-y-2">
        {suggestions.map((s, i) => (
          <li key={i} className="text-sm text-khiimori-text-secondary flex gap-2">
            <span className="text-amber-400">→</span>
            {s}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ComponentBlock({
  comp,
  compact,
}: {
  comp: AppDocumentComponent;
  compact?: boolean;
}) {
  const type = comp.type;

  switch (type) {
    case 'header':
      return (
        <DocHeader
          title={typeof comp.title === 'string' ? comp.title : 'Generated app'}
          subtitle={typeof comp.subtitle === 'string' ? comp.subtitle : undefined}
          compact={compact}
        />
      );
    case 'comparison_table':
    case 'data_table':
      return <ComparisonTableBlock comp={comp} compact={compact} />;
    case 'feature_highlight':
      return <FeatureHighlightBlock comp={comp} />;
    case 'pros_cons':
      return <ProsConsBlock comp={comp} />;
    case 'summary':
      return (
        <p className="text-sm text-khiimori-text-secondary leading-relaxed whitespace-pre-wrap">
          {typeof comp.text === 'string' ? comp.text : ''}
        </p>
      );
    case 'timeline':
      return <TimelineBlock comp={comp} />;
    case 'research_sections':
      return <ResearchSectionsBlock comp={comp} />;
    case 'sources':
      return <SourcesBlock comp={comp} />;
    case 'collections':
      return <CollectionsBlock comp={comp} />;
    case 'entities':
      return <EntitiesBlock comp={comp} />;
    case 'dashboard_metrics':
    case 'dashboard_widgets':
      return <DashboardMetricsBlock comp={comp} compact={compact} />;
    case 'dashboard_sections':
      return <DashboardSectionsBlock comp={comp} />;
    default:
      return null;
  }
}

interface AppDocumentViewProps {
  document: AppDocument;
  compact?: boolean;
}

export function AppDocumentView({ document: doc, compact = false }: AppDocumentViewProps) {
  const title = doc.title ?? doc.name ?? 'Generated app';
  const subtitle = doc.summary ?? doc.description;
  const components = doc.components ?? [];
  const hasHeader = components.some((c) => c.type === 'header');

  return (
    <div className={compact ? 'space-y-2' : 'space-y-6'}>
      {!hasHeader && !compact && <DocHeader title={title} subtitle={subtitle} />}
      {components.map((comp, i) => (
        <ComponentBlock key={`${comp.type}-${i}`} comp={comp} compact={compact} />
      ))}
      {!compact && <SuggestionsBlock suggestions={doc.suggestions ?? []} />}
    </div>
  );
}

export { resolveAppDocument, getAppCardDescription } from '@/components/apps/appDocument';
export type { AppDocument, AppDocumentComponent } from '@/components/apps/appDocument';
